// Library imports
import React, { useCallback, useEffect, useRef, useState } from "react";

// User Component imports
import * as Constants from "_core/configs/Constants";
import * as LocalStorageUtils from "_core/utils/localstorage";
import { PROMPT_GROUPS } from "../utils/promptVersions";
import { MD_TOOLS } from "../utils/promptMarkdown";
import { shortcutLabel } from "../utils/shortcut";
import { markupChecksOf } from "../utils/promptLint";
import { assistVariablesOf, autoSentVariablesOf } from "../utils/promptVariables";
import { clampSplit, parseOutlineState, SPLIT_DEFAULT } from "../utils/promptPanes";
import VariableMenu from "./VariableMenu";
import AutoSentInfo from "./AutoSentInfo";
import PromptPane from "./PromptPane";

// ツールバーから開く変数の候補リストの幅(px)
const VAR_MENU_WIDTH = 320;

// 分割比とアウトラインの開閉は端末に残し、再読み込み後も同じ表示にする
const readSplit = () => clampSplit(LocalStorageUtils.GetItem(Constants.LOCALSTORAGE_KEYS.opsPromptSplit));
const readOutline = () => parseOutlineState(LocalStorageUtils.GetItem(Constants.LOCALSTORAGE_KEYS.opsPromptOutline));
const saveSetting = (key, value) => {
    try {
        LocalStorageUtils.SetItem(key, value);
    } catch (e) {
        // 保存できなくても表示の切り替えは使える
    }
};

/**
 * プロンプト管理の右カラム（グループ切替・書式ツールバー・システム／ユーザーの左右ペイン）
 * 書式ツールと変数の挿入はフォーカス中のペイン（focusTab）に効く
 */
const PromptEditor = ({ groupKey, groups, isUnsaved, focusTab, texts, remotes, issues, onChangeText, onFocusTab, onSelectGroup, onDiscardPane }) => {
    const splitRef = useRef(null);
    const paneRefs = { sys: useRef(null), user: useRef(null) };
    const [split, setSplit] = useState(readSplit);
    const [dragging, setDragging] = useState(false);
    const [outline, setOutline] = useState(readOutline);
    const [activeInfo, setActiveInfo] = useState({ sys: {}, user: {} });
    // ツールバーから開いた変数の候補（{ index }）
    const [varMenu, setVarMenu] = useState(null);

    const group = groups[groupKey];
    const variablesOf = (tab) => {
        const list = assistVariablesOf(groupKey, group?.rules?.[tab]?.template_variables);
        return list.length ? list : null;
    };
    const focusVariables = variablesOf(focusTab);
    // テンプレートとして使われるプロンプトで、システムが後ろに自動で付けて送る情報（最終返答のシステム）
    const autoSent = group?.rules?.[focusTab]?.template_variables ? autoSentVariablesOf(groupKey) : [];
    const markupChecks = markupChecksOf(groupKey, focusTab);
    const { currentLevel, currentListKind } = activeInfo[focusTab] || {};
    const menuItems = varMenu && focusVariables ? focusVariables : [];

    useEffect(() => setVarMenu(null), [groupKey, focusTab]);

    // 候補リストの外をクリックしたら閉じる
    useEffect(() => {
        if (!varMenu) return undefined;
        const onDown = (e) => {
            if (!e.target.closest(".ops-var-menu, .ops-var-tool")) setVarMenu(null);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [varMenu]);

    const onActiveInfo = useCallback((tab, info) => {
        setActiveInfo((prev) => (prev[tab]?.currentLevel === info.currentLevel && prev[tab]?.currentListKind === info.currentListKind
            ? prev : { ...prev, [tab]: info }));
    }, []);

    const handleChange = useCallback((tab, value) => {
        setVarMenu(null);
        onChangeText(tab, value);
    }, [onChangeText]);

    const pickVariable = (name) => {
        setVarMenu(null);
        paneRefs[focusTab].current?.insertVariable(name);
    };

    // 候補を開いているあいだは、編集欄の ↑↓ / Enter・Tab / Esc を候補の操作に使う
    const menuKeyRef = useRef(null);
    menuKeyRef.current = (e) => {
        if (!varMenu || !menuItems.length) return false;
        const plain = !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && !e.nativeEvent.isComposing && e.keyCode !== 229;
        if (!plain) return false;
        const count = menuItems.length;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const step = e.key === "ArrowDown" ? 1 : -1;
            setVarMenu({ index: (varMenu.index + step + count) % count });
            return true;
        }
        if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            pickVariable(menuItems[Math.min(varMenu.index, count - 1)]);
            return true;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            setVarMenu(null);
            return true;
        }
        return false;
    };
    const onMenuKey = useCallback((e) => menuKeyRef.current(e), []);

    // 分割ハンドルのドラッグ（左ペインの幅 20〜80%）
    const startSplit = (e) => {
        const box = splitRef.current;
        if (!box) return;
        e.preventDefault();
        const rect = box.getBoundingClientRect();
        let latest = split;
        const move = (ev) => {
            latest = clampSplit(((ev.clientX - rect.left) / rect.width) * 100);
            setSplit(latest);
        };
        const up = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
            document.body.style.userSelect = "";
            setDragging(false);
            saveSetting(Constants.LOCALSTORAGE_KEYS.opsPromptSplit, String(latest));
        };
        document.body.style.userSelect = "none";
        setDragging(true);
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
    };
    const resetSplit = () => {
        setSplit(SPLIT_DEFAULT);
        saveSetting(Constants.LOCALSTORAGE_KEYS.opsPromptSplit, String(SPLIT_DEFAULT));
    };

    const toggleOutline = useCallback((tab) => {
        setOutline((prev) => {
            const next = { ...prev, [tab]: !prev[tab] };
            saveSetting(Constants.LOCALSTORAGE_KEYS.opsPromptOutline, JSON.stringify(next));
            return next;
        });
    }, []);

    const unsaved = isUnsaved(groupKey);

    const paneProps = (tab) => ({
        ref: paneRefs[tab],
        tab,
        groupKey,
        text: texts[tab],
        remoteText: remotes[tab],
        issues: issues[tab],
        focused: focusTab === tab,
        variables: variablesOf(tab),
        outlineOpen: outline[tab],
        onToggleOutline: toggleOutline,
        onChange: handleChange,
        onFocusPane: onFocusTab,
        onDiscard: onDiscardPane,
        onActiveInfo,
        onMenuKey,
    });

    return (
        <div className="ops-editor">
            <div className="ops-editor-head">
                <div className="ops-editor-switch">
                    <div className="ops-editor-switch-tabs">
                        <div className="ops-seg" role="tablist" title="プロンプトを切り替えます">
                            {PROMPT_GROUPS.map((g) => (
                                <button key={g.key} type="button" role="tab" aria-selected={g.key === groupKey}
                                    className={"ops-seg-btn" + (g.key === groupKey ? " is-active" : "")}
                                    title={isUnsaved(g.key) ? g.name + "（未保存の編集あり）" : g.name} onClick={() => onSelectGroup(g.key)}>
                                    {isUnsaved(g.key) && <span className="ops-seg-dot" />}
                                    {g.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="ops-editor-switch-select">
                        <div className={"ops-select-pill-wrap" + (unsaved ? " has-dot" : "")}>
                            <select className="ops-select-pill is-bold" value={groupKey} onChange={(e) => onSelectGroup(e.target.value)} title="プロンプトを切り替えます">
                                {PROMPT_GROUPS.map((g) => (
                                    <option key={g.key} value={g.key}>{isUnsaved(g.key) && g.key !== groupKey ? "● " + g.name : g.name}</option>
                                ))}
                            </select>
                            <i className="bi bi-chevron-down" />
                            {unsaved && <span className="ops-select-dot" title="未保存の編集があります" />}
                        </div>
                    </div>
                </div>
                <div className="ops-editor-head-right">
                    {unsaved && <span className="ops-unsaved-note"><span className="ops-unsaved-dot" />未保存の編集あり</span>}
                </div>
            </div>

            <div className="ops-editor-toolbar">
                <div className="ops-md-tools">
                    {MD_TOOLS.filter((tool) => !tool.requires || markupChecks.includes(tool.requires)).map((tool) => {
                        const active = (tool.kind === "h" && tool.level === currentLevel) || tool.kind === currentListKind;
                        return (
                            <button key={tool.title} type="button" className={"ops-md-tool" + (active ? " is-active" : "")} title={tool.shortcut ? `${tool.title}（${shortcutLabel(tool.shortcut)}）` : tool.title}
                                onMouseDown={(e) => e.preventDefault()} onClick={() => paneRefs[focusTab].current?.applyTool(tool)}>
                                {tool.icon ? <i className={"bi bi-" + tool.icon} /> : <span>{tool.text}</span>}
                            </button>
                        );
                    })}
                    {focusVariables &&
                        <span className="ops-var-tool">
                            <button type="button" className={"ops-md-tool" + (varMenu ? " is-active" : "")}
                                title="変数を挿入（{ を入力しても候補が出ます）"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setVarMenu(varMenu ? null : { index: 0 })}>
                                <i className="bi bi-braces-asterisk" />
                            </button>
                            {varMenu &&
                                <VariableMenu items={menuItems} activeIndex={Math.min(varMenu.index, Math.max(0, menuItems.length - 1))}
                                    onSelect={pickVariable} onHover={(index) => setVarMenu({ index })}
                                    style={{ top: "calc(100% + 4px)", left: 0, width: VAR_MENU_WIDTH }} />
                            }
                        </span>
                    }
                    {autoSent.length > 0 && <AutoSentInfo items={autoSent} />}
                </div>
                <span className="ops-model-badge" title="このプロンプトを実行するLLMモデル">
                    <i className="bi bi-cpu" />{group?.model || "未設定"}
                </span>
            </div>

            <div className="ops-editor-split" ref={splitRef}>
                <PromptPane {...paneProps("sys")} className="is-first" style={{ "--ops-split": split + "%" }} />
                <div className={"ops-split-handle" + (dragging ? " is-dragging" : "")} title="ドラッグで幅を調整（ダブルクリックで半分ずつに戻す）"
                    onPointerDown={startSplit} onDoubleClick={resetSplit}>
                    <div className="ops-split-line" />
                    <div className="ops-split-grip"><i className="bi bi-grip-vertical" /></div>
                </div>
                <PromptPane {...paneProps("user")} className="is-second" />
            </div>
        </div>
    );
};

export default PromptEditor;
